import { useCallback, useEffect, useRef } from "react";
import { alpha, type Theme } from "@mui/material";
import { useAppTheme } from "@/hooks/useAppTheme";
import { PdfViewer, type PdfViewerProps, type PageDimensions } from "./PdfViewer";

// =============================================================================
// Types — same shape as the original PdfPreview exports so consumers only
// need to update their import paths.
// =============================================================================

/** Bounding box coordinates as [x, y] pairs in **inches** (Azure DI native units). */
type BoundingBox = [number, number][];

/**
 * Field status, shared with the spreadsheet preview legend (SpreadsheetPreview)
 * so both source-mirror surfaces speak one color language.
 */
export type PdfHighlightStatus = "extracted" | "error" | "warning" | "unmapped";

export interface PdfHighlightMetadata {
  pageNumber: number;
  boundingBox: BoundingBox;
  textSpan: string;
}

export interface PdfHighlight {
  fieldName: string;
  value: string | string[];
  /** Drives the polygon color. Defaults to "extracted" (green) when omitted. */
  status?: PdfHighlightStatus;
  /** Renders a heavier stroke — used to mark the active/selected region. */
  emphasized?: boolean;
  metadata: PdfHighlightMetadata;
}

export interface PdfViewerWithHighlightsProps extends Omit<PdfViewerProps, "renderPageOverlay"> {
  /** Field-level bounding box highlights — coordinates in inches (Azure DI units) */
  highlights?: PdfHighlight[];
  /**
   * When true, scroll the active highlight (emphasized, else first) into view
   * (centered) whenever it changes. Lets a caller that drives highlights from a
   * selection (e.g. the PDF remap surface's blocking-alert cycle) keep the
   * document tracking the selected field, not just the page. Off by default so
   * the plain preview drawer keeps its existing scroll behavior.
   */
  scrollToHighlight?: boolean;
  /**
   * Called with a highlight's `fieldName` when its region is clicked. Providing
   * this makes the polygons clickable (pointer cursor); omit for a static
   * overlay.
   */
  onHighlightClick?: (fieldName: string) => void;
}

/** Status → base palette color, matching SpreadsheetPreview's cell legend. */
const statusColor = (theme: Theme, status: PdfHighlightStatus): string => {
  switch (status) {
    case "error":
      return theme.palette.error.main;
    case "warning":
      return theme.palette.warning.main;
    case "unmapped":
      return theme.palette.text.disabled;
    case "extracted":
    default:
      return theme.palette.success.main;
  }
};

// =============================================================================
// Constants
// =============================================================================

/** PDF points per inch — standard conversion factor. */
const POINTS_PER_INCH = 72;

// =============================================================================
// Component
// =============================================================================

export const PdfViewerWithHighlights = ({
  highlights = [],
  scrollToHighlight = false,
  onHighlightClick,
  ...viewerProps
}: PdfViewerWithHighlightsProps) => {
  // useAppTheme (not MUI's useTheme) is the documented Coronis convention — it
  // consolidates the dark-mode check that every consumer would otherwise repeat.
  const { theme, isDarkMode } = useAppTheme();

  // Ref to the active highlight's polygon so we can scroll it into view when the
  // active highlight changes (region-level tracking, not just page-level). Keyed
  // strictly off the *emphasized* highlight — not a first-highlight fallback —
  // so selecting a field that has no coordinates (no highlight of its own)
  // doesn't scroll the document to an unrelated field.
  const focusPolygonRef = useRef<SVGPolygonElement | null>(null);
  const emphasized = highlights.find((h) => h.emphasized);
  const focusKey = emphasized ? `${emphasized.metadata.pageNumber}:${emphasized.fieldName}` : null;

  useEffect(() => {
    if (!scrollToHighlight || !focusKey) return;
    // Defer so the overlay has rendered (canvas dimensions resolve async) and so
    // this wins over PdfViewer's page-level focus scroll, which runs first.
    const timer = setTimeout(() => {
      focusPolygonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    return () => clearTimeout(timer);
  }, [scrollToHighlight, focusKey]);

  /**
   * Render highlight overlays for a single PDF page as an SVG layer.
   *
   * Coordinates arrive as inches (Azure DI native units) and are converted to
   * a percentage of canvas dimensions so the SVG viewBox scales with zoom.
   * Formula: inches × POINTS_PER_INCH × scale = canvas pixels.
   */
  const renderPageOverlay = useCallback(
    (pageNumber: number, dimensions: PageDimensions) => {
      const pageHighlights = highlights.filter((h) => h.metadata.pageNumber === pageNumber);
      if (pageHighlights.length === 0) return null;

      const pxPerInch = POINTS_PER_INCH * dimensions.scale;

      return (
        <svg
          viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
          preserveAspectRatio="none"
          style={{ width: "100%", height: "100%", display: "block" }}
        >
          {pageHighlights.map((highlight, idx) => {
            const { boundingBox } = highlight.metadata;
            if (boundingBox.length === 0) return null;

            const points = boundingBox.map(([x, y]) => `${x * pxPerInch},${y * pxPerInch}`).join(" ");
            // Ref the emphasized highlight (the one scrollToHighlight tracks).
            const isPrimary = focusKey === `${pageNumber}:${highlight.fieldName}`;
            const base = statusColor(theme, highlight.status ?? "extracted");
            const clickable = Boolean(onHighlightClick);

            return (
              <polygon
                key={`${highlight.fieldName}-${idx}`}
                ref={isPrimary ? focusPolygonRef : undefined}
                points={points}
                fill={alpha(base, highlight.emphasized ? (isDarkMode ? 0.3 : 0.22) : isDarkMode ? 0.16 : 0.1)}
                stroke={alpha(base, highlight.emphasized ? 1 : 0.75)}
                strokeWidth={highlight.emphasized ? 2.5 : 1.5}
                // pointer-events is inherited none from the overlay box; opt the
                // polygon back in so only the regions (not the whole page) are
                // clickable, leaving text-selection / scroll intact elsewhere.
                style={clickable ? { pointerEvents: "auto", cursor: "pointer" } : undefined}
                role={clickable ? "button" : undefined}
                tabIndex={clickable ? 0 : undefined}
                aria-label={clickable ? `Select ${highlight.fieldName}` : undefined}
                onClick={clickable ? () => onHighlightClick?.(highlight.fieldName) : undefined}
                onKeyDown={
                  clickable
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onHighlightClick?.(highlight.fieldName);
                        }
                      }
                    : undefined
                }
              >
                {clickable && <title>{highlight.fieldName}</title>}
              </polygon>
            );
          })}
        </svg>
      );
    },
    [highlights, focusKey, theme, isDarkMode, onHighlightClick]
  );

  return <PdfViewer {...viewerProps} renderPageOverlay={renderPageOverlay} />;
};
